import { useContext, Suspense } from 'react';
import React from 'react';
import Scrollbar from 'src/components/Scrollbar';
import { SidebarContext } from 'src/contexts/SidebarContext';

import {
  Box,
  alpha,
  styled,
  Divider,
  useTheme,
  lighten,
  darken,
  Tooltip,
  IconButton,
  SwipeableDrawer,
  useMediaQuery
} from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';

const SidebarMenu = React.lazy(() => import('./SidebarMenu'));
import Logo from 'src/components/LogoSign';
import SidebarEnvFooter from 'src/components/SidebarEnvFooter';

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
        width: ${theme.sidebar.width};
        min-width: ${theme.sidebar.width};
        color: ${theme.colors.alpha.trueWhite[70]};
        position: relative;
        z-index: 7;
        height: 100vh;
        padding-bottom: 0; /* Remove bottom padding to avoid gap */
        display: flex;
        flex-direction: column;
        border-right: none;
        box-shadow: none;
`
);

function Sidebar() {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const scrollRef = React.useRef<any>(null);
  const content = (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', ml: -3, width: '100%' }}>
          <Logo />
        </Box>
        <Divider sx={{ mt: theme.spacing(3), mx: -1.5, mb: 0, background: theme.colors.alpha.trueWhite[10], borderWidth: 1 }} />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'hidden', mt: 0 }}>
        <Scrollbar ref={scrollRef}>
          <Suspense fallback={<Box sx={{ p: 2 }} />}> 
            <SidebarMenu />
          </Suspense>
        </Scrollbar>
      </Box>
      <SidebarEnvFooter scrollRef={scrollRef} />
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <SwipeableDrawer
          open={!sidebarToggle}
          onOpen={() => {}}
          onClose={toggleSidebar}
          anchor="left"
          disableDiscovery={false}
          disableBackdropTransition={typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: theme.sidebar.width,
              borderRight:
                theme.palette.mode === 'dark'
                  ? `1px solid ${alpha(theme.palette.common.black, 0.22)}`
                  : `1px solid ${theme.sidebar.dividerBg}`,
              boxShadow: 'none',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.sidebar.background
                  : darken(theme.colors.alpha.black[100], 0.8)
            }
          }}
        >
          {content}
        </SwipeableDrawer>
      ) : (
        <SidebarWrapper
          sx={{
            display: sidebarToggle ? 'none' : 'inline-block',
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            borderRight:
              theme.palette.mode === 'dark'
                ? `1px solid ${alpha(theme.palette.common.black, 0.22)}`
                : `1px solid ${theme.sidebar.dividerBg}`,
            background:
              theme.palette.mode === 'dark'
                ? theme.sidebar.background
                : darken(theme.colors.alpha.black[100], 0.5),
            boxShadow: 'none'
          }}
        >
          {content}
        </SidebarWrapper>
      )}
    </>
  );
}

export default Sidebar;
